/**
 * DelegationSection component
 * Manage user delegations (who can manage this user's availability)
 */

import { useState } from 'react';
import { useDelegates, useAddDelegate, useRemoveDelegate } from '@/hooks/useDelegates';
import { useUsers } from '@/hooks/useUsers';
import type { User } from '@/types/api';
import { useTranslation } from 'react-i18next';

interface DelegationSectionProps {
  user: User;
}

export function DelegationSection({ user }: DelegationSectionProps) {
  const { t } = useTranslation();
  const { data: delegates, isLoading: delegatesLoading } = useDelegates(user.id);
  const { data: allUsersData, isLoading: usersLoading } = useUsers();
  const addDelegate = useAddDelegate();
  const removeDelegate = useRemoveDelegate();
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  const handleAddDelegate = () => {
    if (!selectedUserId) return;

    addDelegate.mutate(
      {
        userId: user.id,
        delegatedToUserId: parseInt(selectedUserId, 10),
      },
      {
        onSuccess: () => {
          setSelectedUserId('');
        },
      }
    );
  };

  const handleRemoveDelegate = (delegateId: number) => {
    if (confirm(t('users.confirmRemoveDelegate'))) {
      removeDelegate.mutate({
        userId: user.id,
        delegateId,
      });
    }
  };

  if (delegatesLoading || usersLoading) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="space-y-2">
          <div className="h-10 bg-gray-200 rounded" />
          <div className="h-10 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  const allUsers = allUsersData?.users || [];
  const availableUsers = allUsers.filter(
    (u) => u.id !== user.id && !delegates?.find((d) => d.id === u.id)
  );

  return (
    <section className="bg-gray-50 rounded-lg p-6 mt-8">
      <h3 className="text-xl font-semibold text-gray-900 mb-4">
        {t('users.delegationTitle', { name: user.name })}
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        {t('users.delegationDescription')}
      </p>

      {delegates && delegates.length > 0 ? (
        <ul className="space-y-2 mb-6">
          {delegates.map((delegate) => (
            <li
              key={delegate.id}
              className="flex items-center justify-between bg-white rounded-lg border border-gray-200 px-4 py-3"
            >
              <span className="font-medium text-gray-900">{delegate.name}</span>
              <button
                onClick={() => handleRemoveDelegate(delegate.id)}
                disabled={removeDelegate.isPending}
                className="px-3 py-1 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {removeDelegate.isPending ? t('common.removing') : t('common.remove')}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-500 italic mb-6">{t('users.noDelegates')}</p>
      )}

      <div className="flex gap-3">
        <select
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
          disabled={!availableUsers || availableUsers.length === 0 || addDelegate.isPending}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">{t('users.selectPerson')}</option>
          {availableUsers?.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
        <button
          onClick={handleAddDelegate}
          disabled={!selectedUserId || addDelegate.isPending}
          className="px-6 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {addDelegate.isPending ? t('common.adding') : t('common.add')}
        </button>
      </div>

      {addDelegate.isError && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {t('users.addDelegateError')}
        </p>
      )}
      {removeDelegate.isError && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {t('users.removeDelegateError')}
        </p>
      )}
    </section>
  );
}
